import { Injectable, NgZone, inject } from "@angular/core";
import { Subject, timer, observeOn, tap, asapScheduler, queueScheduler } from "rxjs";
import { LoaderState } from "@app/shared/components/loader/loader.model";

@Injectable({
  providedIn: "root"
})
export class LoaderService {
  private zone = inject(NgZone);
  private _showCounter: number = 0;
  private _loaderSubject = new Subject<LoaderState>();
  private _hideLoader: boolean;
  loaderState = this._loaderSubject.asObservable();

  /**
   * Notify subscriber to show loader / spinner
   */
  show() {
    if (this._hideLoader) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this._showCounter += 1;
      timer(125, queueScheduler)
        .pipe(
          observeOn(asapScheduler),
          tap(() => {
            if (this._showCounter > 0) {
              this._loaderSubject.next(<LoaderState>{ show: true });
            }
          })
        )
        .subscribe();
    });
  }

  /**
   * Notify subscriber to hide loader / spinner
   */
  hide(immediate: boolean = false) {
    if (this._hideLoader) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      if (immediate) {
        this.resetLoader();
        return;
      }

      this._showCounter -= 1;

      timer(125, queueScheduler)
        .pipe(
          tap(() => {
            if (this._showCounter < 0) {
              this._showCounter = 0;
            }
            if (this._showCounter === 0) {
              this._loaderSubject.next(<LoaderState>{ show: false });
            }
          }),
          observeOn(asapScheduler)
        )
        .subscribe();
    });
  }

  /**
   * Notify subscriber to hide loader / spinner with no delay
   */
  clear() {
    this.zone.runOutsideAngular(() => {
      this._showCounter = 0;
      this._loaderSubject.next(<LoaderState>{ show: false });
    });
  }

  /**
   * Disable the loader. Nothing will show until toggled back on.
   */
  disableLoader() {
    this._hideLoader = true;
    this.resetLoader();
  }

  /**
   * Enable the loader to show as normal
   */
  enableLoader() {
    this._hideLoader = false;
  }

  /**
   * Reset loader status
   */
  resetLoader() {
    this._showCounter = 0;
    this._loaderSubject.next(<LoaderState>{ show: false });
  }

  get shouldShow(): boolean {
    return this._showCounter > 0;
  }

  get showCounter(): number {
    return this._showCounter;
  }
}
